import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { CalendarIcon, Search, ClipboardList, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuditLogPage = () => {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: 'all',
    entity_type: 'all',
    user_id: '',
    start_date: subDays(new Date(), 7),
    end_date: new Date()
  });

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 200
      };
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.start_date) params.start_date = format(filters.start_date, 'yyyy-MM-dd');
      if (filters.end_date) params.end_date = format(filters.end_date, 'yyyy-MM-dd');

      const response = await axios.get(`${API}/audit`, { params });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      create: 'bg-green-500/20 text-green-400',
      update: 'bg-blue-500/20 text-blue-400',
      delete: 'bg-red-500/20 text-red-400',
      login: 'bg-purple-500/20 text-purple-400',
      logout: 'bg-gray-500/20 text-gray-400',
      submit: 'bg-orange-500/20 text-orange-400',
      approve: 'bg-teal-500/20 text-teal-400',
      resubmit: 'bg-yellow-500/20 text-yellow-400',
      password_change: 'bg-pink-500/20 text-pink-400'
    };
    return styles[action] || 'bg-gray-500/20 text-gray-400';
  };

  const getEntityBadge = (entity) => {
    const styles = {
      user: 'bg-blue-500/20 text-blue-400',
      store: 'bg-green-500/20 text-green-400',
      sales_entry: 'bg-orange-500/20 text-orange-400',
      purchase: 'bg-purple-500/20 text-purple-400',
      fuel_delivery: 'bg-yellow-500/20 text-yellow-400',
      money_movement: 'bg-teal-500/20 text-teal-400'
    };
    return styles[entity] || 'bg-gray-500/20 text-gray-400';
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Audit Log
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all system activities and changes
          </p>
        </div>

        <Button variant="outline" onClick={fetchLogs} data-testid="refresh-audit-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select 
              value={filters.action} 
              onValueChange={(value) => setFilters({ ...filters, action: value })}
            >
              <SelectTrigger data-testid="audit-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="logout">Logout</SelectItem>
                <SelectItem value="submit">Submit</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.entity_type} 
              onValueChange={(value) => setFilters({ ...filters, entity_type: value })}
            >
              <SelectTrigger data-testid="audit-entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="store">Store</SelectItem>
                <SelectItem value="sales_entry">Sales Entry</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="fuel_delivery">Fuel Delivery</SelectItem>
                <SelectItem value="money_movement">Money Movement</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(filters.start_date, 'PP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.start_date}
                  onSelect={(date) => date && setFilters({ ...filters, start_date: date })}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(filters.end_date, 'PP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.end_date}
                  onSelect={(date) => date && setFilters({ ...filters, end_date: date })}
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ClipboardList className="h-12 w-12 mb-4 opacity-50" />
              <p>No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-4 font-medium text-muted-foreground">Timestamp</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Action</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Entity</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-border/50 table-row-hover">
                      <td className="p-4 text-sm">
                        <div>
                          {new Date(log.timestamp).toLocaleDateString('en-GB')}
                        </div>
                        <div className="text-muted-foreground">
                          {new Date(log.timestamp).toLocaleTimeString('en-GB')}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{log.user_name}</div>
                        <div className="text-sm text-muted-foreground">{log.user_email}</div>
                      </td>
                      <td className="p-4">
                        <Badge className={getActionBadge(log.action)}>
                          {log.action}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={getEntityBadge(log.entity_type)}>
                          {log.entity_type?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm max-w-md truncate">
                        {log.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
